/* Stran "Obvestila" — kaj je novega. Bere vsak prijavljen uporabnik,
   pise, ureja in brise samo superadmin.
   [PATCH objave-fe v1]
   [PATCH objave-fe v2] vidna gumba Uredi / Izbriši
   [PATCH objave-fe v3] slike v besedilu (Ctrl+V) + lightbox

   Endpoint je /objave (ne /obvestila) — slednji je zaseden za zvonec.

   VRSTNI RED OB ODPRTJU JE NAMEREN: najprej GET /objave (da oznake "Novo"
   ostanejo vidne v tej seji), sele nato POST /objave/videno, ki pobrise pikico.

   Popravek stare objave pikice NE vrne — backend primerja izkljucno created_at.

   SLIKE: v besedilu stojijo kot zeton [slika:<uuid>]. Bajti se prenasajo z
   avtoriziranim klicem kot blob — <img src> brez glave ne bi delal, ker je
   endpoint zasciten. Isti vzorec kot pri prilogah prijav napak.
   Vsak objectURL se ob odhodu s strani sprosti (revokeObjectURL), sicer bi
   slike ostale v pomnilniku zavihka. */
import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Megaphone, Pencil, Save, Trash2, X } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { api } from '../lib/api';
import { useObjaveStore } from '../stores/objaveStore';

interface Objava {
  id: string;
  naslov: string;
  vsebina: string;
  created_at: string;
  updated_at: string;
  urejeno: boolean;
  novo: boolean;
}

type Del = { tip: 'tekst' | 'slika'; vrednost: string };

const NOVA = '__nova__';
const ZETON = '\\[slika:([0-9a-fA-F-]{36})\\]';
const MAX_BYTOV = 5 * 1024 * 1024;

function datum(s: string): string {
  if (!s) return '';
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleString('sl-SI', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

/* Besedilo razdeli na zaporedje delov: navaden tekst in slike. */
function razdeli(besedilo: string): Del[] {
  const deli: Del[] = [];
  const re = new RegExp(ZETON, 'g');
  let zadnji = 0;
  let m = re.exec(besedilo);
  while (m !== null) {
    if (m.index > zadnji) {
      deli.push({ tip: 'tekst', vrednost: besedilo.slice(zadnji, m.index) });
    }
    deli.push({ tip: 'slika', vrednost: m[1] });
    zadnji = m.index + m[0].length;
    m = re.exec(besedilo);
  }
  if (zadnji < besedilo.length) {
    deli.push({ tip: 'tekst', vrednost: besedilo.slice(zadnji) });
  }
  return deli;
}

function zberiIde(besedilo: string): string[] {
  const re = new RegExp(ZETON, 'g');
  const ids: string[] = [];
  let m = re.exec(besedilo || '');
  while (m !== null) {
    ids.push(m[1]);
    m = re.exec(besedilo || '');
  }
  return ids;
}

/* Prikaz vsebine z vrinjenimi slikami. Manjkajoca slika ne sme podreti
   celotne objave — namesto nje se izrise diskretna oznaka. */
function VsebinaZSlikami({ besedilo, slike, naPovecaj }: {
  besedilo: string;
  slike: Record<string, string>;
  naPovecaj: (url: string) => void;
}) {
  return (
    <div className="text-sm text-gray-700 dark:text-gray-200">
      {razdeli(besedilo).map((d, i) => {
        if (d.tip === 'tekst') {
          return <span key={i} className="whitespace-pre-wrap">{d.vrednost}</span>;
        }
        const url = slike[d.vrednost];
        if (!url) {
          return (
            <span key={i}
              className="inline-block my-2 px-2 py-1 rounded bg-gray-100 dark:bg-gray-800 text-xs text-gray-400">
              (slika ni na voljo)
            </span>
          );
        }
        return (
          <img key={i} src={url} alt="" onClick={() => naPovecaj(url)}
            className="block my-3 max-w-full rounded-lg border border-gray-200 dark:border-gray-700 cursor-zoom-in" />
        );
      })}
    </div>
  );
}

export function ObvestilaPage() {
  const [objave, setObjave] = useState<Objava[]>([]);
  const [nalaganje, setNalaganje] = useState(true);
  const [jeSuperadmin, setJeSuperadmin] = useState(false);
  const [urejam, setUrejam] = useState<string | null>(null);
  const [naslov, setNaslov] = useState('');
  const [vsebina, setVsebina] = useState('');
  const [shranjujem, setShranjujem] = useState(false);
  const [nalagamSliko, setNalagamSliko] = useState(false);
  const [slike, setSlike] = useState<Record<string, string>>({});
  const [povecana, setPovecana] = useState<string | null>(null);
  const oznaciPrebrano = useObjaveStore((s) => s.oznaciPrebrano);

  /* Ref hrani iste objectURL-e kot stanje — potrebujemo ga, ker mora
     ciscenje ob odhodu videti zadnjo vrednost, ne tiste iz prvega izrisa. */
  const slikeRef = useRef<Record<string, string>>({});

  const naloziSlikeZa = async (besedila: string[]) => {
    const vsi: string[] = [];
    for (let i = 0; i < besedila.length; i++) {
      const ids = zberiIde(besedila[i]);
      for (let j = 0; j < ids.length; j++) {
        if (vsi.indexOf(ids[j]) === -1 && !slikeRef.current[ids[j]]) vsi.push(ids[j]);
      }
    }
    if (vsi.length === 0) return;
    const novi: Record<string, string> = {};
    for (let i = 0; i < vsi.length; i++) {
      try {
        const rb = await api.get('/objave/priloge/' + vsi[i], { responseType: 'blob' });
        novi[vsi[i]] = URL.createObjectURL(rb.data);
      } catch {
        /* manjkajoca slika ne sme podreti strani */
      }
    }
    if (Object.keys(novi).length > 0) {
      slikeRef.current = Object.assign({}, slikeRef.current, novi);
      setSlike(slikeRef.current);
    }
  };

  const nalozi = async () => {
    try {
      const r = await api.get('/objave');
      const seznam = (r.data?.objave || []) as Objava[];
      setObjave(seznam);
      setJeSuperadmin(!!r.data?.je_superadmin);
      await naloziSlikeZa(seznam.map((o) => o.vsebina || ''));
    } catch {
      toast.error('Obvestil ni bilo mogoče naložiti.');
    } finally {
      setNalaganje(false);
    }
  };

  useEffect(() => {
    (async () => {
      await nalozi();
      await oznaciPrebrano();
    })();
  }, []);

  /* sprosti vse objectURL-e ob odhodu s strani */
  useEffect(() => {
    return () => {
      const urlji = Object.keys(slikeRef.current);
      for (let i = 0; i < urlji.length; i++) {
        URL.revokeObjectURL(slikeRef.current[urlji[i]]);
      }
    };
  }, []);

  /* Esc zapre lightbox */
  useEffect(() => {
    if (!povecana) return;
    const naTipko = (e: KeyboardEvent) => { if (e.key === 'Escape') setPovecana(null); };
    window.addEventListener('keydown', naTipko);
    return () => window.removeEventListener('keydown', naTipko);
  }, [povecana]);

  const naloziSliko = async (f: File, zac: number, kon: number) => {
    if (f.size > MAX_BYTOV) {
      toast.error('Slika je prevelika (največ 5 MB).');
      return;
    }
    setNalagamSliko(true);
    try {
      const fd = new FormData();
      fd.append('datoteka', f, f.name || 'slika.png');
      const r = await api.post('/objave/priloge', fd);
      const zeton = r.data?.zeton;
      const id = r.data?.id;
      if (!zeton || !id) throw new Error('brez zetona');

      setVsebina((prej) => prej.slice(0, zac) + zeton + prej.slice(kon));

      /* takoj v predogled, da vidiš, kaj si prilepil */
      try {
        const rb = await api.get('/objave/priloge/' + id, { responseType: 'blob' });
        slikeRef.current = Object.assign({}, slikeRef.current,
          { [id]: URL.createObjectURL(rb.data) });
        setSlike(slikeRef.current);
      } catch {
        /* predogled ni kriticen — zeton je ze v besedilu */
      }
      toast.success('Slika dodana.');
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Slike ni bilo mogoče naložiti.');
    } finally {
      setNalagamSliko(false);
    }
  };

  const prilepi = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData ? e.clipboardData.items : null;
    if (!items) return;
    let datoteka: File | null = null;
    for (let i = 0; i < items.length; i++) {
      if (items[i].kind === 'file' && items[i].type.indexOf('image/') === 0) {
        datoteka = items[i].getAsFile();
        break;
      }
    }
    if (!datoteka) return;   /* navadno besedilo — pusti privzeto lepljenje */
    e.preventDefault();
    const el = e.currentTarget;
    const zac = el.selectionStart != null ? el.selectionStart : vsebina.length;
    const kon = el.selectionEnd != null ? el.selectionEnd : zac;
    naloziSliko(datoteka, zac, kon);
  };

  const zacniNovo = () => { setUrejam(NOVA); setNaslov(''); setVsebina(''); };
  const zacniUrejanje = (o: Objava) => { setUrejam(o.id); setNaslov(o.naslov); setVsebina(o.vsebina); };
  const preklici = () => { setUrejam(null); setNaslov(''); setVsebina(''); };

  const shrani = async () => {
    if (!naslov.trim() || !vsebina.trim()) {
      toast.error('Naslov in vsebina sta obvezna.');
      return;
    }
    setShranjujem(true);
    try {
      if (urejam === NOVA) {
        await api.post('/objave', { naslov, vsebina });
        toast.success('Obvestilo objavljeno.');
      } else {
        await api.put('/objave/' + urejam, { naslov, vsebina });
        toast.success('Obvestilo popravljeno.');
      }
      preklici();
      await nalozi();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Shranjevanje ni uspelo.');
    } finally {
      setShranjujem(false);
    }
  };

  const izbrisi = async (o: Objava) => {
    if (!window.confirm('Res izbrišem obvestilo "' + o.naslov + '"?')) return;
    try {
      await api.delete('/objave/' + o.id);
      toast.success('Obvestilo izbrisano.');
      await nalozi();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Brisanje ni uspelo.');
    }
  };

  if (nalaganje) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-400">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Megaphone className="w-6 h-6 text-blue-500 flex-shrink-0" />
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Obvestila</h1>
        {jeSuperadmin && urejam === null && (
          <button onClick={zacniNovo}
            className="ml-auto px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition">
            Novo obvestilo
          </button>
        )}
      </div>

      {jeSuperadmin && urejam !== null && (
        <div className="p-4 rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-950/30 space-y-3">
          <div className="text-sm font-semibold text-gray-700 dark:text-gray-200">
            {urejam === NOVA ? 'Novo obvestilo' : 'Urejanje obvestila'}
          </div>
          <input value={naslov} onChange={(e) => setNaslov(e.target.value)}
            placeholder="Naslov" maxLength={200}
            className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:outline-none focus:border-blue-400" />
          <textarea value={vsebina} onChange={(e) => setVsebina(e.target.value)}
            onPaste={prilepi}
            placeholder="Vsebina — kaj je novega. Sliko dodaš s Ctrl+V." rows={8}
            className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:outline-none focus:border-blue-400" />

          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            {nalagamSliko ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Nalagam sliko …
              </>
            ) : (
              <>Sliko prilepiš s Ctrl+V — največ 10 na obvestilo, do 5 MB.</>
            )}
          </div>

          {zberiIde(vsebina).length > 0 && (
            <div className="p-3 rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Predogled</div>
              <VsebinaZSlikami besedilo={vsebina} slike={slike} naPovecaj={setPovecana} />
            </div>
          )}

          {urejam !== NOVA && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Popravek vidijo vsi, tudi tisti, ki so obvestilo že prebrali — pikice jim ne vrne.
              Če jih želiš znova opozoriti, objavi novo obvestilo.
            </p>
          )}
          <div className="flex gap-2">
            <button onClick={shrani} disabled={shranjujem || nalagamSliko}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition">
              {shranjujem ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Shrani
            </button>
            <button onClick={preklici}
              className="px-3 py-2 rounded-lg text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition">
              Prekliči
            </button>
          </div>
        </div>
      )}

      {objave.length === 0 ? (
        <div className="py-16 text-center text-sm text-gray-400">
          Ni še nobenega obvestila.
        </div>
      ) : (
        <div className="space-y-4">
          {objave.map((o) => (
            <div key={o.id}
              className={clsx(
                'p-4 rounded-xl border bg-white dark:bg-gray-900',
                o.novo
                  ? 'border-blue-300 dark:border-blue-700'
                  : 'border-gray-200 dark:border-gray-800'
              )}>
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="font-semibold text-gray-900 dark:text-white">{o.naslov}</h2>
                    {o.novo && (
                      <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 px-1.5 py-0.5 rounded-full">
                        Novo
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                    {datum(o.created_at)}
                    {o.urejeno ? ' · popravljeno ' + datum(o.updated_at) : ''}
                  </div>
                </div>
                {jeSuperadmin && (
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button onClick={() => zacniUrejanje(o)}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition">
                      <Pencil className="w-3.5 h-3.5" />
                      Uredi
                    </button>
                    <button onClick={() => izbrisi(o)}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-red-300 dark:border-red-800 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950 transition">
                      <Trash2 className="w-3.5 h-3.5" />
                      Izbriši
                    </button>
                  </div>
                )}
              </div>
              <div className="mt-3">
                <VsebinaZSlikami besedilo={o.vsebina} slike={slike} naPovecaj={setPovecana} />
              </div>
            </div>
          ))}
        </div>
      )}

      {povecana && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setPovecana(null)}>
          <button onClick={() => setPovecana(null)} title="Zapri"
            className="absolute top-4 right-4 p-2 rounded-lg bg-white/10 text-white hover:bg-white/20 transition">
            <X className="w-5 h-5" />
          </button>
          <img src={povecana} alt="" onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-full rounded-lg" />
        </div>
      )}
    </div>
  );
}
